import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { Button } from "@tubo/ui/components/ui/button";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Input } from "@tubo/ui/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tubo/ui/components/ui/select";
import { useValueVocabularyList, useForkVocabulary, usePublishVocabulary, getValueVocabularyListQueryKey } from "@tubo/api-models/hooks/value-vocabulary-api.hooks";
import { useSemanticNamespaceList } from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import type { ValueVocabularyListItemVO } from "@tubo/api-models/hooks/value-vocabulary-api.schemas";
import { VocabularyFormDialog } from "@client/components/semantic-platform/vocabulary-form-dialog";
import {
  VocabularyDeleteDialog,
  type VocabularyDeleteTarget,
} from "@client/components/semantic-platform/vocabulary-delete-dialog";
import { NS_CONTEXT_ALL, NS_CONTEXT_GLOBAL, isGlobalNamespace, isPackNamespace } from "@tubo/semantic-concepts";
import { useNsContext } from "@client/components/semantic-platform/namespace-context";
import { ForkDialog } from "@client/components/semantic-platform/fork-dialog";
import { publishStatusLabel, publishStatusVariant } from "@client/components/semantic-platform/publish-status";

export const Route = createFileRoute("/_app/semantic-platform/vocabularies/")({
  // 领域上下文入 URL（缺省=全局；__all__=全部领域聚合浏览）
  validateSearch: (search: Record<string, unknown>): { ns?: string } => {
    return typeof search.ns === "string" && search.ns ? { ns: search.ns } : {};
  },
  component: VocabularyListPage,
});

const PAGE_SIZE = 20;

const TYPE_FILTER_OPTIONS = [
  { value: "all", label: "结构：全部" },
  { value: "flat", label: "结构：平铺" },
  { value: "hierarchical", label: "结构：层级" },
];

function VocabularyListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [keyword, setKeyword] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VocabularyDeleteTarget | null>(null);
  const [forkTarget, setForkTarget] = useState<ValueVocabularyListItemVO | null>(null);

  // 领域上下文（切换器与列表写同一 URL 参数）；聚合浏览模式下提供领域筛选
  const { ns, nsFilter, setNs } = useNsContext();
  const showNsFilter = ns === NS_CONTEXT_ALL;

  const { data, isLoading } = useValueVocabularyList(undefined, { query: {} });
  const { data: namespaces = [] } = useSemanticNamespaceList({ query: {} });
  const nsNameById = useMemo(
    () => new Map(namespaces.map((ns) => [ns.id, ns.name])),
    [namespaces],
  );
  const globalNsIds = useMemo(
    () => new Set(namespaces.filter((nsItem) => isGlobalNamespace(nsItem.code)).map((nsItem) => nsItem.id)),
    [namespaces],
  );
  // 行业包领域 id 集：行只读，编辑触发复制派生
  const packNsIds = useMemo(
    () => new Set(namespaces.filter((nsItem) => isPackNamespace(nsItem.code)).map((nsItem) => nsItem.id)),
    [namespaces],
  );
  const userDomains = useMemo(
    () => namespaces
      .filter((nsItem) => !isPackNamespace(nsItem.code) && !isGlobalNamespace(nsItem.code))
      .map((nsItem) => ({ id: nsItem.id, name: nsItem.name })),
    [namespaces],
  );
  const forkMutation = useForkVocabulary({
    mutation: {
      onSuccess: (detail) => {
        queryClient.invalidateQueries({ queryKey: getValueVocabularyListQueryKey() });
        toast.success(`已复制为「${detail?.name ?? "数据字典"}」（Draft）`);
        setForkTarget(null);
      },
      onError: () => toast.error("复制失败"),
    },
  });
  const publishMutation = usePublishVocabulary({
    mutation: {
      onSuccess: (detail) => {
        queryClient.invalidateQueries({ queryKey: getValueVocabularyListQueryKey() });
        toast.success(`「${detail?.name ?? "数据字典"}」已发布（抽取字段绑定与值归一仅消费已发布词表）`);
      },
      onError: () => toast.error("发布失败"),
    },
  });

  const nsFilterOptions = useMemo(
    () => [
      { value: NS_CONTEXT_ALL, label: "全部领域" },
      { value: NS_CONTEXT_GLOBAL, label: "全局" },
      ...namespaces
        .filter((nsItem) => !isPackNamespace(nsItem.code) && !isGlobalNamespace(nsItem.code))
        .map((nsItem) => ({ value: nsItem.id, label: nsItem.name })),
    ],
    [namespaces],
  );

  useEffect(() => {
    setPage(1);
  }, [keyword, typeFilter, nsFilter]);

  const filteredData = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return (data ?? []).filter((vocabulary) => {
      const matchesKeyword = !normalizedKeyword ||
        [vocabulary.code, vocabulary.name].some((value) => value.toLowerCase().includes(normalizedKeyword));
      const matchesType = typeFilter === "all" ||
        (typeFilter === "hierarchical" ? vocabulary.isHierarchical : !vocabulary.isHierarchical);
      const matchesNs =
        nsFilter === NS_CONTEXT_ALL ||
        (nsFilter === NS_CONTEXT_GLOBAL
          ? !vocabulary.namespaceId || globalNsIds.has(vocabulary.namespaceId)
          : vocabulary.namespaceId === nsFilter);
      return matchesKeyword && matchesType && matchesNs;
    });
  }, [data, globalNsIds, keyword, typeFilter, nsFilter]);

  const columns: DataTableColumn<ValueVocabularyListItemVO>[] = [
    { id: "code", header: "ID", cell: (r) => <span className="font-mono text-sm">{r.code}</span> },
    { id: "name", header: "名称", cell: (r) => (<span className="font-medium">{r.name}{r.origin?.startsWith("pack:") && (<Badge variant="outline" className="ml-1.5 text-[10px]">行业包 · 演示</Badge>)}</span>) },
    {
      id: "namespace",
      header: "所属领域",
      cell: (r) =>
        r.namespaceId ? (
          <Badge variant="outline">{r.namespaceName ?? nsNameById.get(r.namespaceId) ?? r.namespaceId}</Badge>
        ) : (
          <span className="text-xs text-muted-foreground">全局</span>
        ),
    },
    {
      id: "status",
      header: "状态",
      cell: (r) => <Badge variant={publishStatusVariant(r.status)}>{publishStatusLabel(r.status)}</Badge>,
    },
    { id: "itemCount", header: "项数", cell: (r) => r.itemCount },
    {
      id: "isHierarchical",
      header: "类型",
      cell: (r) => (r.isHierarchical ? <Badge variant="outline">层级</Badge> : <Badge variant="secondary">平铺</Badge>),
    },
    { id: "updatedAt", header: "更新时间", cell: (r) => new Date(r.updatedAt).toLocaleString() },
    {
      id: "actions",
      header: "操作",
      cell: (r) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="编辑数据字典"
            title="编辑数据字典"
            onClick={() => (r.namespaceId && packNsIds.has(r.namespaceId) ? setForkTarget(r) : navigate({ to: "/semantic-platform/vocabularies/$id", params: { id: r.id }, search: ns ? { ns } : {} }))}
          >
            <Pencil className="size-3.5" />
          </Button>
          {r.status !== "published" && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={publishMutation.isPending}
              title="发布后可供抽取字段绑定与值归一消费"
              onClick={() => publishMutation.mutate({ id: r.id })}
            >
              发布
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="删除"
            title="删除"
            className="text-destructive"
            onClick={() => setDeleteTarget({ id: r.id, code: r.code, name: r.name })}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  const total = filteredData.length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(total / pageSize)));
  const pagedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">数据字典</span>,
    [],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        {showNsFilter && (
          <Select items={nsFilterOptions} value={nsFilter} onValueChange={(value) => setNs(value ?? NS_CONTEXT_ALL)}>
            <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{nsFilterOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <Select items={TYPE_FILTER_OPTIONS} value={typeFilter} onValueChange={(value) => setTypeFilter(value ?? "all")}>
          <SelectTrigger className="h-8 min-w-28 max-w-48 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{TYPE_FILTER_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="w-56 pl-8" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="搜索名称或编码" />
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" /> 新建字典
        </Button>
      </div>
    ),
    [keyword, typeFilter, nsFilter, nsFilterOptions, showNsFilter, setNs],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="h-full min-h-0 min-w-0">
      <DataTable
        data={pagedData}
        columns={columns}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        page={currentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={total}
        onPageChange={setPage}
        onRowClick={(row) => navigate({ to: "/semantic-platform/vocabularies/$id", params: { id: row.id }, search: ns ? { ns } : {} })}
        emptyState={
          <div className="py-6 text-center text-sm text-muted-foreground">
            <p>暂无符合条件的数据字典</p>
            <p className="mt-1 text-xs">数据字典统一维护枚举型字段的候选取值(如样品形态、制备方法),点击右上角「新建字典」开始</p>
          </div>
        }
      />

      <VocabularyFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultNamespaceId={nsFilter !== NS_CONTEXT_ALL && nsFilter !== NS_CONTEXT_GLOBAL ? nsFilter : undefined}
      />
      <VocabularyDeleteDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} />

      <ForkDialog
        open={forkTarget !== null}
        onOpenChange={(v) => !v && setForkTarget(null)}
        sourceLabel={forkTarget ? forkTarget.name : undefined}
        domains={userDomains}
        onFork={async (targetNamespaceId) => {
          if (!forkTarget) return;
          await forkMutation.mutateAsync({ id: forkTarget.id, data: { targetNamespaceId } });
          return forkTarget.id;
        }}
      />
    </div>
  );
}
